<template>
    <div>
        <CustomerModal 
            @disconnected="onDisconnected"
            @connected="onGekkoAccountantConnected"
            @refreshlist="refreshConnections()"
            :showModalOn="['CustomerOverview']"/>

        <SetColorAndLogoReminder v-if="!currentCompany.accountant_branding.color || !currentCompany.accountant_branding.logo" />

        <FilterBar 
            cookieName="AccountantConnectionsFilterOptionsVue" 
            :mainFilters="['SearchFilter', 'AccountantConnectionStatusFilter']"
            filterBarType="accountant-connections" 
            @change="filtersChanged" >
            <template v-slot:right-bar>
                <TotalAmountsAccountants/>
            </template>
        </FilterBar>
        
        <div class="content--inner">
            <b-table
                :items="items"
                :fields="fields"
                @row-clicked="customerClicked"
                striped hover
                thead-class="d-none"
                :class="['ttable gk-table gk-table--responsive gk-table--striped gk-table--hover']"
                :busy="loading"
                show-empty
                table-busy>
                <template #empty="">
                    <span class="empty-table-text" v-translate>No connections found.</span>
                </template>
                <template #table-busy="">
                    <b-skeleton-table :rows="10" :columns="4" :table-props="{ bordered: false, striped: true }" :hide-header="true" />
                </template>
                <template #cell(name)="data">
                    <div class="avatar-col pr-1">
                        <b-avatar class="avatar-color" :text="data.item.name[0]" />
                    </div>
                    <div class="avatar-col pl-1">
                        <span>{{ data.item.name }}</span>
                    </div>
                </template>
                <template #cell(created)="data">
                    {{data.item.created | formatDate}}
                </template>
                <template #cell(status)="data">
                    <div class="pull-right">
                        <i v-if="isConnected(data.item)" :title="$gettext('Connected')" class="fa fa-chain" style="color: #37A0CD"></i>
                        <i v-if="!isConnected(data.item)" :title="$gettext('Disconnected')" class="fa fa-unlink" style="color: #999"></i>
                    </div>
                </template>
            </b-table>
        </div>
            
        <GekkoButton v-if="nextListUrl" id="load_more_button" :text="$gettext('Load more customers')" icon="fa fa-plus" type="link" />
    </div>
</template>

<script>
import FilterBar from "@/components/list_filters/FilterBar";
import AccountantsService from '@/store/services/accountants.service';
import CustomerModal from '@/components/accountants/CustomerModal';
import GekkoButton from '@/components/GekkoButton';
import TotalAmountsAccountants from "@/components/accountants/TotalAmountsAccountants";
import SetColorAndLogoReminder from "@/components/accountants/SetColorAndLogoReminder.vue";
import { mapGetters } from "vuex";

export default {
    name: "CustomerList", 
    components: { FilterBar, CustomerModal, GekkoButton, TotalAmountsAccountants, SetColorAndLogoReminder },
    data () {
        return {
            items: [],
            fields: [
                { key: "name", tdClass: "avatar-cell" },
                { key: "customer_name" },
                { key: "customer_email" },
                { key: "status", tdClass: "table-cell" }
            ],
            currentFilters: null,
            loading: true,
            nextListUrl: null,
            fetchingCustomers: false
        }
    },
    computed: {
        ...mapGetters({
            currentCompany: 'main/getCurrentCompany'
        })
    },
    created () {
        window.addEventListener('scroll', this.handleScroll);
    },
    destroyed () {
        window.removeEventListener('scroll', this.handleScroll);
    },
    methods: {
        customerClicked (item) {
            this.$router.push({ name: "CustomerOverview", params: { customerId: item.customer.id } });
        },
        filtersChanged (filters) {
            this.currentFilters = filters;
            this.loading = true;
            this.getAccountantConnections(filters);
        },
        refreshConnections () {
            this.filtersChanged(this.currentFilters);
        },
        getAccountantConnections (filters) {
            AccountantsService.getAccountantConnectionsV3(filters).then((response => {
                this.items = response.data.results;
                this.items = this.items.map(item => {
                    item['customer_name'] = item.customer.created_by.display_name
                    item['customer_email'] = item.customer.email
                    return item
                })
                this.nextListUrl = response.data.next;
                this.loading = false;
                this.setPendingStatus();
            }));
        },
        setPendingStatus () {
            AccountantsService.getPendingInvites().then((response) => {
                this.pendingInvites = response.data;
                this.items.forEach(item => {
                    if (this.pendingInvites.find(o => o.invite_to === item.customer_email && item.status !== 'connected')) item.status = 'pending';
                });
            });
        },
        onDisconnected (accountantConnection) {
            for (var i = 0; i < this.items.length; i++) {
                if (this.items[i].id === accountantConnection.id) {
                    this.$set(this.items, i, accountantConnection);
                    break;
                }
            }
            this.refreshConnections()
        },
        isConnected (item) {
            return item.status === "connected";
        },
        isInViewport (element_id) {
            let element = document.getElementById(element_id);
            let rect = element.getBoundingClientRect();
            let html = document.documentElement;
            return (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || html.clientHeight) &&
                rect.right <= (window.innerWidth || html.clientWidth)
            );
        },
        handleScroll () {
            if (!this.fetchingCustomers && this.nextListUrl) {
                let loadNewCustomers = this.isInViewport('load_more_button');

                if (loadNewCustomers) {
                    this.fetchingCustomers = true;
                
                    AccountantsService.listNextPage(this.nextListUrl).then((response) => {
                        this.fetchingCustomers = false
                        let newItems = response.data.results.map(item => {
                            item['customer_name'] = item.customer.created_by.display_name
                            item['customer_email'] = item.customer.email
                            return item
                        })
                        this.items = this.items.concat(newItems)
                        this.nextListUrl = response.data.next
                    });
                }
            }
        },
        addGekkoConnection (item) {
            for (var i = 0; i < this.items.length; i++) {
                if (this.items[i].id === item.id) {
                    this.items[i].status = "connected";
                    return;
                }
            }
            this.items.unshift(item);
        },
        onGekkoAccountantConnected (connection) {
            this.addGekkoConnection(connection);
            this.refreshConnections();
        }
    }
}
</script>

<style lang="scss" scoped>
.empty-table-text {
    color: $colorEmptyList;
    font-style: italic;
}

.avatar-col {
    min-width: 50px;
    display: inline;
}

.avatar-color{
    background-color: $colorUnassigned;
}
</style>

<style lang="scss">
.avatar-cell {
    max-width: 150px;
    text-overflow: ellipsis;
    overflow-x: hidden;
}

.table-cell {
    vertical-align: middle !important;
}

.notes-text {
    font-style: italic;
}
</style>